import React, { useState, useEffect, useCallback } from 'react';
import './InventoryTracker.css';
import { API_URL as API_BASE } from '../api/api';
import GradientHeading from './GradientHeading';

const InventoryTracker = () => {
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch SKUs from backend (real-time inventory for current user)
  const fetchSkus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/inventory/real-time-inventory`, {
        credentials: 'include',
        withCredentials: true,
        // Note: fetch does not support 'withCredentials', that's axios. This is correct for fetch.
      });
      const data = await response.json();
      if (data.status === 'success' && Array.isArray(data.low_stock_items)) {
        // Transform backend data to match expected format
        const transformedSkus = data.low_stock_items.map(item => ({
          id: item.sku_id,
          name: item.sku_name,
          stock_count: item.current_stock,
          low_stock_threshold: item.threshold,
          brand_id: item.brand_id
        }));
        setSkus(transformedSkus);
      } else {
        setSkus([]);
      }
    } catch (error) {
      console.error('Error fetching SKUs:', error);
      setSkus([]);
    } finally {
      setLoading(false);
    }
  }, []);


  // Fetch SKUs on component mount
  useEffect(() => {
    fetchSkus();
  }, [fetchSkus]);

  useEffect(() => {

    }, []);
    
  const updateStock = async (skuId, newStock) => {
    try {
      const response = await fetch(
        `${API_BASE}/inventory/update-stock?sku_id=${skuId}&stock_count=${newStock}`,
        { method: 'POST',
          credentials: 'include',
          withCredentials: true,
        }
      );
      const data = await response.json();
      if (data.status === 'success') {
        fetchSkus(); // Refresh SKUs
        alert('Stock updated successfully!');
      } else {
        alert('Failed to update stock: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating stock:', error);
      alert('Error updating stock');
    }
  };


  const updateThreshold = async (skuId, newThreshold) => {
    try {
      const response = await fetch(
        `${API_BASE}/inventory/update-threshold?sku_id=${skuId}&threshold=${newThreshold}`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (data.status === 'success') {
        fetchSkus(); // Refresh SKUs
        alert('Threshold updated successfully!');
      } else {
        alert('Failed to update threshold: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating threshold:', error);
      alert('Error updating threshold');
    }
  };

  const getStockStatus = (stock, threshold) => {
    if (stock === 0) return 'out-of-stock';
    if (stock <= threshold) return 'low-stock';
    return 'in-stock';
  };

  const getStockStatusColor = (status) => {
    switch (status) {
      case 'out-of-stock': return '#dc3545';
      case 'low-stock': return '#ffc107';
      case 'in-stock': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getStockStatusText = (status) => {
    switch (status) {
      case 'out-of-stock': return '🚨 Out of Stock';
      case 'low-stock': return '⚠️ Low Stock';
      case 'in-stock': return '✅ In Stock';
      default: return '❓ Unknown';
    }
  };


  return (
    <div className="inventory-tracker-simple">
      {/* Header */}
      <div className="inventory-header">
        <div className="header-left">
          <GradientHeading>Inventory Tracker</GradientHeading>
          <p className="subtitle">Monitor and manage your inventory levels</p>
        </div>
      </div>

      {/* Inventory Table - always shown */}
      <div className="table-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading inventory data...</p>
          </div>
        ) : (
          <table className="inventory-table">
            <thead>
              <tr>
                <th>SKU Name</th>
                <th>Current Stock</th>
                <th>Threshold</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {skus.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', fontStyle: 'italic', color: '#888' }}>
                    No SKUs found for your brand.
                  </td>
                </tr>
              ) : (
                skus.map(sku => {
                  const status = getStockStatus(sku.stock_count, sku.low_stock_threshold);
                  return (
                    <tr key={sku.id} className={`sku-row ${status}`}>
                      <td className="sku-name">
                        <div className="sku-info">
                          <strong>{sku.name}</strong>
                          <small>ID: {sku.id}</small>
                        </div>
                      </td>
                      <td className="stock-count">
                        <input
                          type="number"
                          value={sku.stock_count}
                          onChange={(e) => {
                            const newStock = parseInt(e.target.value) || 0;
                            updateStock(sku.id, newStock);
                          }}
                          className="stock-input"
                          min="0"
                        />
                      </td>
                      <td className="threshold">
                        <input
                          type="number"
                          value={sku.low_stock_threshold}
                          onChange={(e) => {
                            const newThreshold = parseInt(e.target.value) || 0;
                            updateThreshold(sku.id, newThreshold);
                          }}
                          className="threshold-input"
                          min="0"
                        />
                      </td>
                      <td className="status">
                        <span 
                          className="status-indicator"
                          style={{ backgroundColor: getStockStatusColor(status) }}
                        >
                          {getStockStatusText(status)}
                        </span>
                      </td>
                      <td className="actions">
                        <div className="action-buttons">
                          <button
                            onClick={() => {
                              const newStock = prompt('Enter new stock count:', sku.stock_count);
                              if (newStock !== null && newStock !== '') {
                                updateStock(sku.id, parseInt(newStock) || 0);
                              }
                            }}
                            className="btn btn-sm btn-edit"
                            title={'Edit stock count'}
                          >
                            📝 Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Summary Stats */}
      {skus.length > 0 && (
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-label">Total SKUs:</span>
            <span className="stat-value">{skus.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Low Stock:</span>
            <span className="stat-value warning">
              {skus.filter(sku => getStockStatus(sku.stock_count, sku.low_stock_threshold) === 'low-stock').length}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Out of Stock:</span>
            <span className="stat-value danger">
              {skus.filter(sku => sku.stock_count === 0).length}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryTracker;